import { useListContext, useRecordContext, useSaveContext } from "ra-core"
import { FC, useCallback, useMemo } from "react"
import { RecPatch } from "@/dataProvider/tools"
import { DataCatalogResponseDto, UpdateFieldGovernanceInput } from "@/gen/api/governance"
import { useDataCatalogContext } from "../dataCatalogContext/DataCatalogContext"
import { FieldEditContext, FieldEditControllerProps } from "./FieldEditContext"
import Edit from "@/controller/Edit"
import { MutationCallbacks } from "@/dataProvider/types"
import { useDefaultSavedHandlers } from "@/controller/defaultSavedHandlers"
import { decideDataTypeMaskingKind } from "./maskingRuleDataTypesMap"


export const FieldRecordEditContextProvider: FC = ({ children }) => {
  const record = useRecordContext<DataCatalogResponseDto&RecPatch>()
  const { cancelSingleEdit } = useDataCatalogContext()
  const { refetch } = useListContext()
  const handlers = useDefaultSavedHandlers('Field Edit')
  handlers.onSuccess.merge(useCallback(() => {
    cancelSingleEdit(record)
    refetch()
  }, []))
  
  return (
    <Edit 
      id={record.id} 
      resource="governance/data-catalog" 
      basePath="" 
      mutationMode="pessimistic" 
      {...handlers}
    >
      <FieldRecordEditInner>
        {children}
      </FieldRecordEditInner>
    </Edit>
  )
}

const FieldRecordEditInner: FC = ({ children }) => {
  const { getFieldEncryptRules } = useDataCatalogContext()
  const record = useRecordContext<DataCatalogResponseDto&RecPatch>()
  const { save } = useSaveContext()
  
  const value: FieldEditControllerProps = useMemo(() => {
    const saveField = (x: UpdateFieldGovernanceInput, callbacks?: MutationCallbacks) => {
      // x.secondaryClassificationId === -1, from FieldEdit, means to remove classification
      if (x.secondaryClassificationId === -1) {
        x.secondaryClassificationId = undefined
        x.primaryClassification = undefined
      }
      if (save && record) {
        save({ 
          ...x, 
          databaseId: record.databaseId, 
          tableId: record.tableId, 
          fieldId: record.fieldId,
        }, 
        '',  
        callbacks)
      }
    }
    const encryptRules = getFieldEncryptRules(record)
    
    return {
      save: saveField,
      encryptRules,
      maskingKind: decideDataTypeMaskingKind(record?.type || ''),
      mode: 'single',
    }
  }, [save, record?.id])
  
  return (
    <FieldEditContext.Provider value={value}>
      {children}
    </FieldEditContext.Provider>
  )
}

